import { getBackendUrl } from './backendUrl';
import { BaseModel, KappaConfig, TariffConfiguration } from '../types/model';

export interface ChatProposal {
  id: string;
  name: string;
  explanation: string;
  assumptions: string[];
  framework: string;
  dataset: string;
  tariff_rules: { importers: string[]; exporters: string[]; sectors: string[]; rate: number; mode: 'Increment' | 'Set Level' }[];
  tariffConfig: TariffConfiguration;
  kappaConfig: KappaConfig | null;
}
export interface ChatRun {
  id: string;
  name: string;
  status: 'running' | 'done' | 'error';
  error?: string;
  config: ChatProposal;
}
export interface ChatSession {
  title?: string;
  usage?: { last?: { totalTokens?: number }; modelContextWindow?: number };
  context_sources?: string[];
  activity?: string;
  page_request?: import('../utils/pageAgent').PageAction | null;
  status: 'preparing' | 'idle' | 'thinking' | 'solving' | 'compacting' | 'error';
  messages: { id: string; role: 'user' | 'assistant' | 'system'; text: string }[];
  proposal: ChatProposal | null;
  runs: ChatRun[];
  error: string | null;
  context_version: string;
  framework: string;
  dataset: string;
}

async function request<T>(path: string, token?: string, body?: unknown): Promise<T> {
  const base = await getBackendUrl();
  const response = await fetch(`${base}/research-chat${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(typeof data.detail === 'string' ? data.detail : `Chat request failed (${response.status}).`);
  }
  return response.json();
}

export const researchChat = {
  availability: () => request<{ enabled: boolean }>('/availability'),
  start: (framework = 'CP2015', dataset = 'calibration_2017_40c') => request<ChatSession & { token: string }>('/sessions', undefined, { framework, dataset }),
  poll: (token: string) => request<ChatSession>('/session', token),
  send: (token: string, text: string) => request('/messages', token, { text }),
  claimPage: (token:string, request_id:string, client_id:string) => request<{claimed:boolean}>('/page-claim',token,{request_id,client_id}),
  pageResult: (token:string, request_id:string, client_id:string, result:unknown) => request('/page-result',token,{request_id,client_id,result}),
  stop: (token:string) => request<ChatSession>('/stop',token,{}),
  compact: (token:string) => request<ChatSession>('/compact',token,{}),
  rename: (token:string,title:string) => request<ChatSession>('/rename',token,{title}),
  confirm: (token: string, proposal_id: string) => request('/confirm', token, { proposal_id }),
  result: (token: string, run: string) => request<{ framework: string; dataset: string; run: ChatRun & { result: BaseModel } }>(`/results/${run}`, token),
};
